import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import FileUpload from '../components/FileUpload';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { profileService, resumeService } from '../services/api';
import SkeletonLoader from '../components/ui/SkeletonLoader';

function ProfilePage() {
    const { user, setUser } = useAuth();
    const { t, i18n } = useTranslation();

    const [form, setForm] = useState({
        profile: {
            full_name: user?.profile?.full_name || '',
            university: user?.profile?.university || '',
            degree: user?.profile?.degree || '',
            graduation_year: user?.profile?.graduation_year || '',
            bio: user?.profile?.bio || '',
        }
    });

    const [permanentCV, setPermanentCV] = useState(null);
    const [loadingCV, setLoadingCV] = useState(true);
    const [uploadingCV, setUploadingCV] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [cvSuccess, setCvSuccess] = useState('');
    const [cvError, setCvError] = useState('');

    useEffect(() => { fetchPermanentCV(); }, []);

    const fetchPermanentCV = async () => {
        try {
            const response = await resumeService.list();
            const permanent = response.data.find(r => r.storage_type === 'permanent');
            setPermanentCV(permanent || null);
        } catch (err) {
            console.error(t('profile.errors.load_cv_failed'));
        } finally {
            setLoadingCV(false);
        }
    };

    const handleChange = (field, value) => {
        setForm(prev => ({
            ...prev,
            profile: { ...prev.profile, [field]: value }
        }));
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        const cleanForm = {
            profile: {
                full_name: form.profile.full_name.trim(),
                university: form.profile.university.trim(),
                degree: form.profile.degree.trim(),
                graduation_year: form.profile.graduation_year || null,
                bio: form.profile.bio.trim(),
            }
        };
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await profileService.update(cleanForm);
            setUser(response.data);
            setSuccess(t('profile.saved'));
        } catch (err) {
            setError(t('profile.errors.update_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleCVUpload = async (e) => {
        e.preventDefault();
        const file = e.target.cv_file.files[0];
        if (!file) { setCvError(t('profile.errors.select_file')); return; }
        const allowedTypes = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];
        if (!allowedTypes.includes(file.type)) { setCvError(t('profile.errors.file_type')); return; }
        if (file.size > 10 * 1024 * 1024) { setCvError(t('profile.errors.file_size')); return; }
        if (permanentCV) {
            try { await resumeService.delete(permanentCV.id); }
            catch (err) { setCvError(t('profile.errors.replace_failed')); return; }
        }
        const formData = new FormData();
        formData.append('resume', file);
        formData.append('is_cv', true);
        setUploadingCV(true);
        setCvError('');
        setCvSuccess('');
        try {
            await resumeService.upload(formData);
            setCvSuccess(t('profile.cv_uploaded'));
            e.target.reset();
            fetchPermanentCV();
        } catch (err) {
            setCvError(t('profile.errors.upload_failed'));
        } finally {
            setUploadingCV(false);
        }
    };

    const handleDeleteCV = async () => {
        if (!window.confirm(t('profile.delete_cv_confirm'))) return;
        try {
            await resumeService.delete(permanentCV.id);
            setPermanentCV(null);
            setCvSuccess(t('profile.cv_deleted'));
        } catch (err) {
            setCvError(t('profile.errors.delete_failed'));
        }
    };

    const locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';

    return (
        <Layout>
            <div className="page-container">
                <h1>{t('profile.settings_title')}</h1>
                <p className="subtitle">{t('profile.settings_subtitle')}</p>

                <div className="card">
                    <div className="profile-header">
                        <div className="avatar">
                            {user?.profile?.full_name
                                ? user.profile.full_name.charAt(0).toUpperCase()
                                : user?.email?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h2>{user?.profile?.full_name || t('profile.your_name')}</h2>
                            <p>{user?.email}</p>
                        </div>
                    </div>

                    {error && <div className="error-message">{error}</div>}
                    {success && <div className="success-message">{success}</div>}

                    <form onSubmit={handleProfileSubmit}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>{t('profile.full_name')}</label>
                                <input type="text" value={form.profile.full_name}
                                    onChange={e => handleChange('full_name', e.target.value)}
                                    placeholder={t('profile.placeholders.full_name')} />
                            </div>
                            <div className="form-group">
                                <label>{t('profile.university')}</label>
                                <input type="text" value={form.profile.university}
                                    onChange={e => handleChange('university', e.target.value)}
                                    placeholder={t('profile.placeholders.university')} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>{t('profile.degree')}</label>
                                <input type="text" value={form.profile.degree}
                                    onChange={e => handleChange('degree', e.target.value)}
                                    placeholder={t('profile.placeholders.degree')} />
                            </div>
                            <div className="form-group">
                                <label>{t('profile.graduation_year')}</label>
                                <input type="number" value={form.profile.graduation_year}
                                    onChange={e => handleChange('graduation_year', e.target.value)}
                                    placeholder={t('profile.placeholders.graduation_year')} min="2020" max="2030" />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>{t('profile.bio')}</label>
                            <textarea value={form.profile.bio}
                                onChange={e => handleChange('bio', e.target.value)}
                                placeholder={t('profile.placeholders.bio')}
                                rows={4} />
                        </div>
                        <button type="submit" disabled={loading} className="btn-primary">
                            {loading ? t('profile.saving') : t('profile.save_changes')}
                        </button>
                    </form>
                </div>

                <div className="card">
                    <h2>{t('profile.cv_title')}</h2>
                    <p className="subtitle">
                        {t('profile.cv_subtitle')}
                    </p>

                    {cvError && <div className="error-message">{cvError}</div>}
                    {cvSuccess && <div className="success-message">{cvSuccess}</div>}

                    {loadingCV ? (
                        <p>{t('profile.loading_cv')}</p>
                    ) : permanentCV ? (
                        <div className="cv-current">
                            <div className="cv-info">
                                <span>📄</span>
                                <div>
                                    <strong>{permanentCV.file_name}</strong>
                                    <small>{t('profile.uploaded_on')} {new Date(permanentCV.uploaded_at).toLocaleDateString(locale)}</small>
                                </div>
                            </div>
                            {permanentCV.score && (
                                <div className="score-section">
                                    <div className="score-item"><span>{t('resume.ats_score')}</span><strong>{permanentCV.score.ats_score}%</strong></div>
                                    <div className="score-item"><span>{t('resume.matched_keywords')}</span><strong>{permanentCV.score.keyword_score}%</strong></div>
                                    <div className="score-item"><span>{t('analytics.avg_readability')}</span><strong>{permanentCV.score.readability_score}%</strong></div>
                                </div>
                            )}
                            <button onClick={handleDeleteCV} className="btn-danger">{t('profile.delete_cv')}</button>
                        </div>
                    ) : (
                        <p className="empty-state">{t('profile.no_cv')}</p>
                    )}

                    <form onSubmit={handleCVUpload} style={{ marginTop: '1rem' }}>
                        <div className="form-group">
                            <label>{permanentCV ? t('profile.replace_cv_label') : t('profile.upload_cv_label')}</label>
                            <FileUpload
                                name="cv_file"
                                accept=".pdf,.docx"
                                label={permanentCV ? t('profile.choose_replacement_cv') : t('profile.choose_cv')}
                            />
                        </div>
                        <button type="submit" disabled={uploadingCV} className="btn-primary">
                            {uploadingCV ? t('profile.uploading') : permanentCV ? t('profile.replace_cv') : t('profile.upload_cv')}
                        </button>
                    </form>
                </div>
            </div>
        </Layout>
    );
}

export default ProfilePage;